/**
 * SaleLookup - Page publique de consultation de vente via QR code
 * Accessible à tous (sans authentification)
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';

interface SaleLine {
  product_name: string;
  quantity: number | string;
  unit_price_cents: number;
  tax_rate: number | string;
  is_return?: boolean;
}

interface SaleData {
  sale_number: string;
  created_at: string;
  total_ttc_cents: number;
  total_ht_cents: number;
  total_tva_cents: number;
  payment_method: string;
  status: string;
  lines: SaleLine[];
}

function toNum(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function formatCurrency(cents: number | string | null | undefined): string {
  return (toNum(cents) / 100).toFixed(2) + ' €';
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('fr-BE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getPaymentMethodLabel(method: string): string {
  const labels: Record<string, string> = {
    cash: 'Espèces',
    bancontact: 'Bancontact',
    card: 'Carte bancaire',
    wallet: 'Portefeuille',
    mixed: 'Paiement mixte',
    meal_voucher: 'Chèques repas',
    wero: 'Wero',
    online: 'Payé en ligne',
  };
  return labels[method] || method;
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Validée</span>;
    case 'cancelled':
      return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">Annulée</span>;
    case 'refunded':
      return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">Remboursée</span>;
    default:
      return <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">{status}</span>;
  }
}

export default function SaleLookup() {
  const { saleId } = useParams<{ saleId: string }>();
  const [sale, setSale] = useState<SaleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!saleId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await api.get(`/pos/sales/lookup/${saleId}`);
        setSale(data);
      } catch (err: any) {
        console.error('Erreur chargement vente:', err);
        setError(err.response?.data?.message || 'Impossible de charger cette vente');
      } finally {
        setLoading(false);
      }
    })();
  }, [saleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-emerald-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Chargement du ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-amber-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ticket introuvable</h1>
          <p className="text-gray-600 mb-6">{error || 'Impossible de charger ce ticket'}</p>
          <a
            href="https://foliesboutik.be/shop"
            className="inline-block px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
          >
            Visiter la boutique en ligne
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-8 py-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold">Les Folies Maraîchères</h1>
                <p className="text-emerald-100 mt-1">Ticket N° {sale.sale_number}</p>
              </div>
              {getStatusBadge(sale.status)}
            </div>
          </div>

          <div className="px-8 py-6">
            <div className="mb-6 pb-6 border-b border-gray-200">
              <p className="text-sm text-gray-600">Date d'achat</p>
              <p className="font-semibold text-gray-900">{formatDate(sale.created_at)}</p>
            </div>

            {/* Articles */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Articles</h2>
              <div className="space-y-3">
                {sale.lines.map((line, index) => {
                  const qty = toNum(line.quantity);
                  const unitPrice = toNum(line.unit_price_cents);
                  const lineTotal = qty * unitPrice;
                  const signedTotal = line.is_return ? -lineTotal : lineTotal;
                  const taxRatePct = Math.round(toNum(line.tax_rate) * 100);
                  return (
                    <div key={index} className="flex items-start justify-between py-3 border-b border-gray-100">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {line.product_name}
                          {line.is_return && <span className="text-xs text-orange-600 ml-2">(Retour)</span>}
                        </p>
                        <p className="text-sm text-gray-600">
                          {qty} × {formatCurrency(unitPrice)} · TVA {taxRatePct}%
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900 ml-4">
                        {formatCurrency(signedTotal)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totaux */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-2">
              <div className="flex justify-between text-gray-700">
                <span>Sous-total HT</span>
                <span>{formatCurrency(sale.total_ht_cents)}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>TVA</span>
                <span>{formatCurrency(sale.total_tva_cents)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-gray-900 pt-3 border-t border-gray-300">
                <span>Total TTC</span>
                <span>{formatCurrency(sale.total_ttc_cents)}</span>
              </div>
            </div>

            {/* Paiement */}
            <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Mode de paiement</span>
                <span className="font-semibold text-gray-900">
                  {getPaymentMethodLabel(sale.payment_method)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <a
            href="https://foliesboutik.be/shop"
            className="block w-full px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-center transition-colors"
          >
            🛒 Visiter la boutique en ligne
          </a>
          <a
            href="https://foliesboutik.be/shop/login?mode=register"
            className="block w-full px-8 py-3 bg-white border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-lg font-semibold text-center transition-colors"
          >
            👤 Créer un compte client
          </a>
          <p className="text-xs text-gray-500 text-center pt-2">
            Un compte vous permet de commander en ligne, bénéficier d'avantages fidélité et suivre vos commandes
          </p>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Les Folies Maraîchères SRL · Rue du Commerce 147, 7370 Dour · T.V.A. 0715954426
        </p>
      </div>
    </div>
  );
}
